import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField, SwitchFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Separator,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm, useFormState } from '@repo/shared-client/form'
import { UserRole } from '@repo/shared-common'
import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { availableThemes, isThemeName, type SavedThemeName, themesSelectableByRole } from '@web/src/theme'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  ThemeSettingsByProfileIdQuery,
  ThemeSettingsByProfileIdQueryVariables,
  UpdateThemeSettingsMutation,
  UpdateThemeSettingsMutationVariables,
} from 'types/graphql'

const THEME_SETTINGS_QUERY: TypedDocumentNode<ThemeSettingsByProfileIdQuery, ThemeSettingsByProfileIdQueryVariables> =
  gql`
  query ThemeSettingsByProfileId($profileId: String!) {
    themeSettings: themeSettingsByProfileId(profileId: $profileId) {
      theme
      seasonalThemesEnabled
    }
  }
`

const UPDATE_THEME_SETTINGS: TypedDocumentNode<UpdateThemeSettingsMutation, UpdateThemeSettingsMutationVariables> = gql`
  mutation UpdateThemeSettings($profileId: String!, $theme: String!, $seasonalThemesEnabled: Boolean!) {
    updateThemeSettings(profileId: $profileId, theme: $theme, seasonalThemesEnabled: $seasonalThemesEnabled) {
      theme
      seasonalThemesEnabled
    }
  }
`

type ThemeSettings = NonNullable<ThemeSettingsByProfileIdQuery['themeSettings']>

const selectableTheme = (theme: string, isAdmin: boolean): SavedThemeName => {
  const canonical = isThemeName(theme, 'light') ? 'preview-light' : theme
  const options = themesSelectableByRole(isAdmin)
  if (options.includes(canonical as SavedThemeName)) {
    return canonical as SavedThemeName
  }
  return 'dark'
}

export const Route = createFileRoute('/(auth)/(account-layout)/account/$profileId/theme')({
  component: AccountThemePage,
  head: () =>
    seo({
      title: `Theme | ${env.VITE_APP_NAME}`,
      description: 'Theme',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, THEME_SETTINGS_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef }
  },
})

function AccountThemePage() {
  const { t } = useTranslation()
  const { profileId } = Route.useParams()
  const { queryRef } = Route.useLoaderData()
  const apolloClient = useApolloClient()
  const router = useRouter()
  const { isAtLeastRole, refreshSession } = useAuth()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<ThemeSettingsByProfileIdQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading
  const [showSpinner, setShowSpinnerValue] = useState(false)

  const isAdmin = isAtLeastRole(UserRole.ADMIN)
  const canSelectTheme = isAtLeastRole(UserRole.SUPPORTER)
  const settings = data?.themeSettings as ThemeSettings | undefined
  const savedTheme = settings?.theme
  const savedSeasonalThemesEnabled = settings?.seasonalThemesEnabled

  const formMethods = useForm<{ theme: string; seasonalThemesEnabled: boolean }>({
    defaultValues: {
      theme: selectableTheme(settings?.theme ?? 'dark', isAdmin),
      seasonalThemesEnabled: settings?.seasonalThemesEnabled ?? true,
    },
  })
  const { isDirty } = useFormState({ control: formMethods.control })

  useEffect(() => {
    if (savedTheme === undefined || savedSeasonalThemesEnabled === undefined) {
      return
    }
    formMethods.reset({
      theme: selectableTheme(savedTheme, isAdmin),
      seasonalThemesEnabled: savedSeasonalThemesEnabled,
    })
  }, [savedTheme, savedSeasonalThemesEnabled, isAdmin, formMethods])

  const [updateThemeSettings] = useMutation(UPDATE_THEME_SETTINGS, {
    onCompleted: async () => {
      toast.success(t('Theme Updated'))
      await apolloClient.refetchQueries({ include: [THEME_SETTINGS_QUERY] })
      await refreshSession()
      await router.invalidate()
      setShowSpinnerValue(false)
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
  })

  const onSubmit = (fieldData: { theme: string; seasonalThemesEnabled: boolean }) => {
    if (!settings) {
      return
    }
    setShowSpinnerValue(true)
    updateThemeSettings({
      variables: {
        profileId,
        theme: canSelectTheme ? fieldData.theme : settings.theme,
        seasonalThemesEnabled: fieldData.seasonalThemesEnabled,
      },
    })
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-destructive">{t('No Data')}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <Form formMethods={formMethods} onSubmit={onSubmit}>
        <CardHeader className="gap-2">
          <CardTitle>{t('Theme')}</CardTitle>
          <Separator />
        </CardHeader>
        <CardContent className="flex flex-col gap-4 py-4">
          <SwitchFormField
            helperText={t('When enabled, the theme for the current season replaces your selected theme.')}
            label={t('Seasonal themes')}
            name="seasonalThemesEnabled"
          />
          <NativeSelectFormField
            disabled={!canSelectTheme}
            helperText={
              canSelectTheme
                ? t('Supporters can choose the theme used when seasonal themes are off, or outside a seasonal period.')
                : undefined
            }
            label={t('Theme')}
            name="theme"
          >
            {availableThemes(isAdmin, t).map((theme) => (
              <option key={theme.name} value={theme.name}>
                {theme.label}
              </option>
            ))}
          </NativeSelectFormField>
          {!canSelectTheme && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-muted-foreground">{t('You need to be a supporter to choose a theme.')}</p>
              <Button
                className="shrink-0"
                nativeButton={false}
                render={<Link to="/supporter" />}
                size="sm"
                type="button"
                variant="default"
              >
                {t('Become a Supporter')}
              </Button>
            </div>
          )}
        </CardContent>
        <CardFooter className="justify-end">
          <Button disabled={!isDirty || showSpinner} type="submit">
            {showSpinner ? <Spinner className="mr-2 size-4" /> : null}
            {t('Save')}
          </Button>
        </CardFooter>
      </Form>
    </Card>
  )
}
